import { BiDraftResult } from '@euler/model/bi/draft';
import {
  BiDelta,
  BiDeltaTrend,
  BiFormatSpec,
  biDelta,
  formatBiValue,
} from '@euler/model/bi/format';
import { BiCell } from '@euler/model/bi/result';
import { BiDeltaDirection, BiKpiCompare } from '@euler/model/bi/viz';
import { BiColumnRef, biColumnLookup } from './biColumns';

/** One headline number of a KPI: its value and how it moved. */
export interface BiKpiReading {
  ref: BiColumnRef;
  current: BiCell;
  /** The previous period's value; undefined without a comparison. */
  previous: BiCell | undefined;
  text: string;
  /** Null when the look hides it or nothing is compared. */
  delta: BiDelta | null;
  /** Whether the move is good news, as the card says a rise is. */
  good: boolean;
}

/**
 * Reads the measure `key` of a one-row result, and against the previous
 * period when the viz compares; null when the result lacks the column.
 */
export function biKpiReading(
  result: BiDraftResult,
  key: string,
  format: BiFormatSpec | undefined,
  compare: BiKpiCompare | undefined,
  look: { showDelta: boolean; deltaGoodWhen: BiDeltaDirection },
): BiKpiReading | null {
  const formats = format ? { [key]: format } : undefined;
  const ref = biColumnLookup(result.current, formats)(key);
  if (!ref) return null;
  const current = result.current.rows[0]?.[ref.index] ?? null;
  const previousRef =
    compare === BiKpiCompare.PreviousPeriod && result.previous
      ? biColumnLookup(result.previous, formats)(key)
      : null;
  const previous =
    previousRef && result.previous
      ? result.previous.rows[0]?.[previousRef.index] ?? null
      : undefined;
  // A percent moves in percentage points, anything else by its share.
  const delta =
    previous === undefined || !look.showDelta
      ? null
      : biDelta(current, previous, ref.format);
  // Whether a rise is good news is the card's to say (a cost falls).
  const good =
    delta !== null &&
    (look.deltaGoodWhen === BiDeltaDirection.Up
      ? delta.trend === BiDeltaTrend.Up
      : delta.trend === BiDeltaTrend.Down);
  return {
    ref,
    current,
    previous,
    text: formatBiValue(current, ref.format),
    delta,
    good,
  };
}
